import React, { useMemo, useState } from "react";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";

import { useGetAllMonitorValVersOre } from "../monitorisorse/Services";
import { MonitorValVersOre } from "../monitorisorse/types";

interface ListaMonitorValVersOreProps {}

type MonitorValVersOreRow = {
  id: string;
  ref: string;
  codprog: string;
  desprog: string;
  totreport: number;
  numvalidati: number;
  numnonvalidati: number;
  isTotal?: boolean;
  emailclientetimereport: string;
};

const ListaMonitorValVersOre: React.FC<
  ListaMonitorValVersOreProps
> = () => {
  const isIframe = window.self !== window.top;
  const today = new Date();

  const [columnFilters] =
    useState<MRT_ColumnFiltersState>([]);

  const [sorting] = useState<MRT_SortingState>([]);

  const [pagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const [globalFilter] = useState("");

  const [anno, setAnno] = useState(
    today.getFullYear()
  );

  const [mese, setMese] = useState(
    today.getMonth() + 1
  );

  const mesi = [
    "Gennaio",
    "Febbraio",
    "Marzo",
    "Aprile",
    "Maggio",
    "Giugno",
    "Luglio",
    "Agosto",
    "Settembre",
    "Ottobre",
    "Novembre",
    "Dicembre",
  ];

  const anni = useMemo(() => {
    const currentYear = today.getFullYear();

    return Array.from(
      { length: currentYear - 2020 + 1 },
      (_, index) => 2020 + index
    );
  }, []);

  // Filtri anno e mese inviati all'API
  const effectiveFilters = useMemo(
    () => [
      ...columnFilters,
      { id: "anno", value: anno },
      { id: "mese", value: mese },
    ],
    [columnFilters, anno, mese]
  );

  // Chiamata API
  const { data, isLoading } =
    useGetAllMonitorValVersOre(
      pagination,
      effectiveFilters,
      globalFilter,
      sorting
    );

  const results = data?.results ?? [];

  // Raggruppamento dati per referente e progetto
 const rows = useMemo<MonitorValVersOreRow[]>(() => {
  const map: Record<string, MonitorValVersOreRow> = {};

  results.forEach((item: MonitorValVersOre) => {
    const referente = item.ref?.trim() || "NN";
    const codprog = item.codprog?.trim() || "NN";
    const desprog = item.desprog?.trim() || "NN";
    const emailclientetimereport = item.emailclientetimereport?.trim() || "NN";

    const key = `${referente}-${codprog}`;

    if (!map[key]) {
      map[key] = {
        id: key,
        ref: referente,
        codprog,
        desprog,
        totreport: 0,
        numvalidati: 0,
        numnonvalidati: 0,
        emailclientetimereport,
      };
    }

    map[key].totreport += Number(item.totreport ?? 0);
    map[key].numvalidati += Number(item.numvalidati ?? 0);
    map[key].numnonvalidati += Number(
      item.numnonvalidati ?? 0
    );
  });

  const groupedRows = Object.values(map).sort((a, b) => {
    if (a.ref === "NN" && b.ref !== "NN") return -1;
    if (b.ref === "NN" && a.ref !== "NN") return 1;

    const refCompare = a.ref.localeCompare(b.ref, "it");

    if (refCompare !== 0) {
      return refCompare;
    }

    return a.codprog.localeCompare(b.codprog);

  });

  const totalRow: MonitorValVersOreRow = {
    id: "__TOTAL__",
    ref: "TOTALE",
    codprog: "",
    desprog:"",
    totreport: groupedRows.reduce(
      (sum, row) => sum + row.totreport,
      0
    ),
    numvalidati: groupedRows.reduce(
      (sum, row) => sum + row.numvalidati,
      0
    ),
    numnonvalidati: groupedRows.reduce(
      (sum, row) => sum + row.numnonvalidati,
      0
    ),
    isTotal: true,
    emailclientetimereport: "",
  };

  return [...groupedRows, totalRow];
}, [results]);
  // Colonne della tabella
  const columns = useMemo<GridColDef<MonitorValVersOreRow>[]>(
    () => [
      {
        field: "ref",
        headerName: "Referente",
        width: 100,

      },

      {
        field: "codprog",
        headerName: "Cod.Prog.",
        width: 100,

      },
      {
        field: "desprog",
        headerName: "Des.Prog.",
        width: 500,

      },

      {
        field: "totreport",
        headerName: "Tot. Report",
        type: "number",
        width: 115,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <span style={{ fontWeight: "bold" }}>
            {Number(params.value ?? 0)}
          </span>
        ),
      },
      {
        field: "numvalidati",
        headerName: "Validati",
        type: "number",
        width: 105,
        headerAlign: "center",
        align: "center",
        cellClassName: "validati-cell",
      },
      {
        field: "numnonvalidati",
        headerName: "Non validati",
        type: "number",
        width: 125,
        headerAlign: "center",
        align: "center",
        cellClassName: (params) =>
          Number(params.value ?? 0) > 0
            ? "nonvalidati-cell"
            : "",
      },
      {
        field: "emailclientetimereport",
        headerName: "Email Cliente",
        width: 200,

      },

    ],
    []
  );

  return (
    <Box
      sx={{
        mt: isIframe ? 0 : "60px",
        minHeight: isIframe
          ? "100vh"
          : "calc(100vh - 60px)",
        boxSizing: "border-box",
        backgroundColor: isIframe
          ? "#fff"
          : undefined,
        px: 2,
        py: 1,
      }}
    >
      {/* Filtri */}
            <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
        <Select
          value={anno}
          onChange={(event) =>
            setAnno(Number(event.target.value))
          }
          size="small"
          sx={{ width: 140 }}
        >
          {anni.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={mese}
          onChange={(event) =>
            setMese(Number(event.target.value))
          }
          size="small"
          sx={{ width: 160 }}
        >
          {mesi.map((nome, index) => (
            <MenuItem
              key={index + 1}
              value={index + 1}
            >
              {nome}
            </MenuItem>
          ))}
        </Select>
      </Box>



      {/* Tabella centrata */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          overflowX: "auto",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          autoHeight
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableRowSelectionOnClick
          disableColumnSorting
          disableColumnResize
          checkboxSelection={false}
          isCellEditable={() => false}
          hideFooter
          density="compact"
          getRowId={(row) => row.id}
          getRowClassName={(params) => {
            if (params.row.isTotal) {
              return "total-row";
            }

            if (params.row.ref === "NN") {
              return "nn-row";
            }

            return "";
          }}
          rowHeight={30}
          columnHeaderHeight={38}
          sx={{
  width: 555,
  maxWidth: "100%",
  backgroundColor: "#fff",

  "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
    display: "none",
  },

  "& .MuiDataGrid-cell": {
    borderRight: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
  },

  "& .MuiDataGrid-columnHeaders": {
    borderBottom: "1px solid #bdbdbd",
  },

  "& .MuiDataGrid-columnHeader": {
    borderRight: "1px solid #e0e0e0",
  },

  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    whiteSpace: "normal",
    lineHeight: 1.2,
    overflow: "visible",
    textOverflow: "unset",
  },

  "& .validati-cell": {
    color: "#2e7d32",
    fontWeight: "bold",
  },

  "& .nonvalidati-cell": {
    color: "#d32f2f",
    fontWeight: "bold",
  },

  "& .nn-row .MuiDataGrid-cell": {
    fontWeight: "bold",
  },

  "& .total-row .MuiDataGrid-cell": {
    fontWeight: "bold",
    backgroundColor: "#eeeeee",
    borderTop: "2px solid #9e9e9e",
  },

  "& .total-row:hover .MuiDataGrid-cell": {
    backgroundColor: "#eeeeee",
  },

  border: "1px solid #bdbdbd",
}}     />
      </Box>
    </Box>
  );
};

export default ListaMonitorValVersOre;